import { use, startTransition, useCallback, useEffect, useMemo } from "react";
import { ToastQueue } from "@react-spectrum/s2";
import {
  useMutation,
  useQueryClient,
  isCancelledError,
} from "@tanstack/react-query";

import { store } from "@app/store";
import { videoKey } from "@app/hooks/videos";
import { presignURLQueryOptions } from "@app/hooks/s3";
import { useImageLabeling } from "@app/hooks/zoltar";
import { useCampaignEditing } from "@app/hooks/useCampaignEditing";
import { useImageVariations } from "@app/hooks/imageVariations";
import {
  loadingImageLabeling,
  labelingId,
} from "@app/hooks/imageVariations/shared";
import {
  MUSIC_BED_VOLUME,
  DEFAULT_VOICEOVER_VOLUME,
} from "@app/constants/audio";
import { VideoWizardContext } from "@app/views/projects/videoWizardContext";
import { getStructureDependencies } from "@app/views/projects/variationDependencies";
import {
  WIZARD_STEP,
  getKeyVisualSyncState,
  getStepEnterAction,
  getStepLeaveAction,
  getStructureVariationKeys,
  isBrandingVariationsStep,
  isCampaignVisualsStep,
  isImagesStep,
} from "@app/views/projects/wizardSteps";

/** No stage at all — the images pipeline is idle outside an images step. */
const NO_STAGES = [];

const filterAssetsForBannerRegeneration = (assets) =>
  assets?.filter((asset) => {
    if (["Logo_cropped"].includes(asset.name)) {
      return false;
    }

    if (asset.name.includes(" #")) {
      return false;
    }

    return true;
  });

const AUDIO_MUX_DOWNLOAD_TEMPLATE_IDS = new Set(["ucx", "ao-overview"]);

export const supportsAudioMuxDownloadTemplate = (templateId) =>
  AUDIO_MUX_DOWNLOAD_TEMPLATE_IDS.has(templateId);

const getAudioMuxSourcesKey = (sourceAssets, musicPath) => {
  const voiceKey = sourceAssets
    .map((a) => {
      const cfg = a?.voiceConfig ?? {};
      const parts = a?.parts ?? {};
      const cfgStr = JSON.stringify(cfg);
      const partsStr = JSON.stringify(parts);
      return [
        a?.filePath ?? "",
        String(a?.generatedAt ?? ""),
        a?.voiceId ?? "",
        cfgStr,
        partsStr,
      ].join("_");
    })
    .join(",");
  return `${voiceKey}\${musicPath ?? ""}`;
};

const getSelectedCampaign = (videoData) =>
  videoData?.campaigns?.find(
    (campaign) => campaign.id === videoData?.selectedCampaignIds?.[0],
  );

const getKeyVisualChangedPrompt = (asset) =>
  `The campaign's key visual (KV-primary) has been replaced, so the current \`KV-description\` no longer describes it. Update \`KV-description\` to describe the new key visual and adjust the other parts of the campaign (\`KV-theme\`, \`KV-theme-short\`, \`KV-location\`, \`KV-character\`, \`KV-product\`, \`KV-alternative-version-description\`, \`KV-without-campaign-theme\`, \`Additional-prompt-Foundry\`, \`Custom-model-prompt\` and any copy that refers to the visual) so the whole campaign stays consistent with it.

New key visual prompt:
${asset?.prompt || "(none — the key visual was uploaded manually)"}

New key visual description:
${JSON.stringify(asset?.labeling ?? {})}`;

/**
 * Encapsulates every step-action callback and the dispatch plumbing that
 * `wizardSteps.js` actions need. Returns a unified `stepActionCtx` so every
 * `isReady(ctx)`, `isPending(ctx)`, and `run(ctx)` call receives the same
 * object.
 *
 * All dependencies are read from `VideoWizardContext` — call this hook from
 * within the provider's subtree.
 */
export const useWizardStepActions = () => {
  const {
    setSelectedTemplateKey,
    video,
    template,
    assetsTemplate,
    createVideo,
    createVideoIfNeeded,
    updateVideo,
    updateAssets,
    uploadAssets,
    customerBranding,
    editCampaign,
    generateBanners,
    exportBanners,
    generateVideo,
    describeMogrtTemplate,
    generateScenes,
    regenerateScenes,
    checkCustomer,
    audioMux,
    videoId,
    setProjectId,
    templateId,
    selectedCampaign,
    selectedPersona,
    customerName,
    hint,
    taglinesData,
    assetsFingerprint,
    bannersAssetsFingerprintRef,
    audioMuxRanForSourcesKeyRef,
    setAudioMuxDownloadUrl,
    runStepEnteringActionRef,
    runStepLeavingActionRef,
    currentStep,
    activeSteps,
  } = use(VideoWizardContext);

  const queryClient = useQueryClient();
  const activeTemplateId = template.data?.id ?? templateId;

  // ---------------------------------------------------------------------------
  // Key visual sync — one mutation, so the step after the key visuals can wait
  // on a single `isPending`: describe the key visual when it has not been yet,
  // then — when it is not the one the campaign's `KV-description` describes —
  // have the LLM adjust the campaign to it.
  // ---------------------------------------------------------------------------

  const { handleSubmitAsync } = useCampaignEditing();
  const keyVisualLabeling = useImageLabeling();

  const labelKeyVisual = async (asset) => {
    const id = labelingId(videoId, asset.name);

    if (loadingImageLabeling.has(id)) {
      return;
    }

    loadingImageLabeling.add(id);

    try {
      const presign = await queryClient.query(
        presignURLQueryOptions(asset.filePath),
      );

      if (!presign?.presignURL) {
        return;
      }

      const labeling = await keyVisualLabeling.mutateAsync({
        key: asset.name,
        imageUrl: presign.presignURL,
        systemPromptId: "common-image-description",
      });

      const latestAsset =
        queryClient
          .getQueryData(videoKey.detail(videoId))
          ?.assets?.find((a) => a?.name === asset.name) ?? asset;

      await updateAssets.mutateAsync({
        assets: [
          {
            ...latestAsset,
            labeling: { ...latestAsset.labeling, ...labeling },
          },
        ],
      });
    } finally {
      loadingImageLabeling.delete(id);
    }
  };

  const syncKeyVisual = useMutation({
    mutationFn: async () => {
      const getVideoData = () =>
        queryClient.getQueryData(videoKey.detail(videoId));

      const initial = getKeyVisualSyncState(
        getVideoData(),
        getSelectedCampaign(getVideoData()),
      );

      if (initial.needsLabeling) {
        await labelKeyVisual(initial.asset);
      }

      const videoData = getVideoData();
      const sourceCampaign = getSelectedCampaign(videoData);
      const { asset, effectiveDescription, isOutOfSync } =
        getKeyVisualSyncState(videoData, sourceCampaign);

      if (!isOutOfSync) {
        return;
      }

      const previousCampaignIds = new Set(
        videoData?.campaigns?.map((campaign) => campaign.id),
      );

      await handleSubmitAsync(getKeyVisualChangedPrompt(asset), videoData, {
        editScope: "key-visuals",
      });

      // Pin the adjusted campaign to the key visual it was written for, so the
      // next time the step is left it reads as in sync.
      const updatedVideoData = getVideoData();
      const adjustedCampaign =
        updatedVideoData?.campaigns?.findLast(
          (campaign) => !previousCampaignIds.has(campaign.id),
        ) ?? getSelectedCampaign(updatedVideoData);

      if (!adjustedCampaign) {
        return;
      }

      await updateVideo.mutateAsync({
        campaigns: updatedVideoData.campaigns.map((campaign) =>
          campaign.id === adjustedCampaign.id
            ? { ...campaign, "KV-description": effectiveDescription }
            : campaign,
        ),
        selectedCampaignIds: [adjustedCampaign.id],
      });
    },
    meta: {
      errorMessage:
        "Failed to adjust the campaign to the new key visual. Please try again.",
    },
  });

  const runSyncKeyVisual = () => {
    if (!syncKeyVisual.isPending) {
      syncKeyVisual.mutate();
    }
  };

  // ---------------------------------------------------------------------------
  // Image variations — owned here rather than by the step's view, so the
  // pipeline is reachable from `enter.run()` at the moment the step is entered
  // (its view is not mounted yet) and keeps running while it is displayed.
  // ---------------------------------------------------------------------------

  const currentStepEntry = useMemo(
    () => activeSteps?.find((step) => step.id === currentStep),
    [activeSteps, currentStep],
  );
  const isOnImagesStep = isImagesStep(currentStepEntry ?? currentStep);
  // There may be several campaign visuals steps, each with its own structure.
  const isOnCampaignVisualsStep = isCampaignVisualsStep(currentStepEntry);
  // The branding step may show variations of its own, generated on demand.
  const isOnBrandingVariationsStep = isBrandingVariationsStep(currentStepEntry);
  const ownsStructureVariations =
    isOnCampaignVisualsStep || isOnBrandingVariationsStep;

  // An images step may cover a subset of the template's stages — or all of
  // them, when it declares no `stages` at all.
  const imagesStepStages = useMemo(() => {
    if (!isOnImagesStep) {
      return NO_STAGES;
    }

    return currentStepEntry?.stages;
  }, [isOnImagesStep, currentStepEntry]);

  // The campaign visuals step (and a branding step with variations) owns the
  // variations its campaign structure shows, by key, whatever stage they
  // belong to.
  const campaignVisualsStructure = useMemo(
    () =>
      ownsStructureVariations ? currentStepEntry?.campaignStructure : null,
    [ownsStructureVariations, currentStepEntry],
  );

  const campaignVisualsDependencies = useMemo(
    () =>
      ownsStructureVariations
        ? getStructureDependencies(campaignVisualsStructure)
        : null,
    [ownsStructureVariations, campaignVisualsStructure],
  );

  const campaignVisualsKeys = useMemo(
    () =>
      ownsStructureVariations
        ? getStructureVariationKeys(campaignVisualsStructure)
        : null,
    [ownsStructureVariations, campaignVisualsStructure],
  );

  const imageVariations = useImageVariations({
    projectId: videoId,
    video,
    templateData: template.data,
    stages: imagesStepStages,
    keys: campaignVisualsKeys ?? undefined,
    sourceDependencies: campaignVisualsDependencies ?? undefined,
    selectedCampaign,
    // The visuals are written for the campaign, so they wait for one — and for
    // it to be adjusted to a changed key visual.
    enabled:
      !syncKeyVisual.isPending &&
      (isOnImagesStep || (ownsStructureVariations && !!selectedCampaign?.id)),
    manual: isOnBrandingVariationsStep,
  });

  // Generating the images & text takes a while — tell the user for as long as
  // some variations are still to be generated. The effect cleanup closes the
  // toast when the step is left or the generation is done. Once the "assets
  // ready" email has been sent there is nothing left to wait for.
  const shouldShowImagesAndTextToast =
    currentStep === WIZARD_STEP.IMAGES_AND_TEXT &&
    !!video.isSuccess &&
    !video.data?.assetsReadyEmailSent &&
    !!template.data &&
    template.data?.id === "one-adobe" &&
    !!selectedCampaign?.id &&
    !syncKeyVisual.isPending &&
    imageVariations.pendingVariations.length > 0;

  useEffect(() => {
    if (!shouldShowImagesAndTextToast) {
      return undefined;
    }

    // No timeout, so the toast is persistent.
    return ToastQueue.info(
      "This will take around 15 minutes. We’ll email you when everything’s ready!",
    );
  }, [shouldShowImagesAndTextToast]);

  // ---------------------------------------------------------------------------
  // Callbacks — each mirrors its former position in VideoWizard.js
  // ---------------------------------------------------------------------------

  const ensureProjectId = useCallback(() => {
    if (videoId) {
      return videoId;
    }
    const id = crypto.randomUUID();
    setProjectId(id);
    return id;
  }, [videoId, setProjectId]);

  const resetPersonaCampaigns = useCallback(() => {
    if (
      !!video.data?.id &&
      !!template.data?.personas?.length &&
      selectedPersona?.id !== video.data?.personaId
    ) {
      updateVideo.mutate({
        personaId: selectedPersona?.id,
        customer: {
          name: video.data?.customer?.name,
        },
        assets: [],
        campaigns: [],
        selectedCampaignIds: [],
      });
    }
  }, [
    template.data?.personas?.length,
    selectedPersona?.id,
    video.data?.id,
    video.data?.personaId,
    video.data?.customer?.name,
    updateVideo.mutate,
  ]);

  const runGenerateBranding = useCallback(
    (_videoData) => {
      const customerName =
        _videoData?.customer?.name ?? video.data?.customer?.name;
      const fullCustomerName =
        _videoData?.customer?.["Company-name"] ??
        video.data?.customer?.["Company-name"];

      const hasCustomerOrHintChanged =
        video.isSuccess &&
        ((!!customerName && video.data?.customer?.name !== customerName) ||
          (!!hint && video.data?.hint !== hint));

      const shouldGenerateBranding =
        (!checkCustomer.isPending && !!customerName && !fullCustomerName) ||
        hasCustomerOrHintChanged;

      if (shouldGenerateBranding) {
        checkCustomer.mutate(
          {
            customerName:
              _videoData?.customer?.name ?? video.data?.customer?.name,
            hint: _videoData?.hint ?? video.data?.hint,
          },
          {
            onSuccess: async (data, variables, onMutateResult, { client }) => {
              const videoData = client.getQueryData(
                videoKey.detail(videoId ?? _videoData?.id),
              );
              const updatePromises = [];

              const {
                "Company-name": name,
                "Brand-color": brandColor,
                "Secondary-color": secondaryColor,
                "Company-website-URL": websiteURL,
              } = data;

              updatePromises.push(
                updateVideo.mutateAsync({
                  customer: {
                    ...(videoData?.customer ?? {}),
                    name,
                    websiteURL,
                    ...data,
                  },
                }),
              );

              const assetsUpdate = new Map();

              if (
                brandColor &&
                !videoData?.assets?.some(
                  (asset) => asset.name === "Brand-color",
                )
              ) {
                assetsUpdate.set("Brand-color", {
                  name: "Brand-color",
                  type: "color",
                  color: brandColor,
                });
              }

              if (
                secondaryColor &&
                !videoData?.assets?.some(
                  (asset) => asset.name === "Secondary-color",
                )
              ) {
                assetsUpdate.set("Secondary-color", {
                  name: "Secondary-color",
                  type: "color",
                  color: secondaryColor,
                });
              }

              if (assetsUpdate.size) {
                updatePromises.push(
                  updateAssets.mutateAsync({
                    assets: Array.from(assetsUpdate.values()),
                  }),
                );
              }

              return await Promise.all(updatePromises);
            },
          },
        );
      }
    },
    [
      videoId,
      video.isSuccess,
      video.data?.customer?.name,
      video.data?.hint,
      customerName,
      hint,
      updateVideo.mutateAsync,
      updateAssets.mutateAsync,
      checkCustomer.isPending,
      checkCustomer.mutate,
    ],
  );

  const {
    isPending: isCustomerBrandingPending,
    refetch: refetchCustomerBranding,
  } = customerBranding;

  const runCreateCampaign = useCallback(
    (videoData) => {
      const hasCustomerOrHintChanged =
        video.isSuccess &&
        (video.data?.customer?.name !== customerName ||
          video.data?.hint !== hint);

      const shouldCreateCampaign =
        !isCustomerBrandingPending && videoData
          ? !videoData?.campaigns?.length
          : (video.isSuccess && !video.data?.campaigns?.length) ||
            hasCustomerOrHintChanged;

      if (shouldCreateCampaign) {
        refetchCustomerBranding(videoData);
      }
    },
    [
      isCustomerBrandingPending,
      video.isSuccess,
      video.data?.campaigns?.length,
      video.data?.customer?.name,
      video.data?.hint,
      customerName,
      hint,
      refetchCustomerBranding,
    ],
  );

  const runGenerateScenes = useCallback(() => {
    if (
      !generateScenes.isPending &&
      video.isSuccess &&
      !!selectedCampaign?.script &&
      !selectedCampaign?.scenes?.length
    ) {
      regenerateScenes();
    }
  }, [
    generateScenes,
    regenerateScenes,
    video.isSuccess,
    selectedCampaign?.script,
    selectedCampaign?.scenes?.length,
  ]);

  const regenerateBannersFromCurrentAssets = useCallback(async () => {
    const videoData = queryClient.getQueryData(videoKey.detail(videoId));

    await updateVideo.mutateAsync({
      assets: filterAssetsForBannerRegeneration(videoData?.assets),
      // templateAssetsRenditions: [],
    });

    if (!generateBanners.isPending) {
      generateBanners.mutate({
        videoId,
      });
    }
  }, [
    videoId,
    queryClient,
    updateVideo.mutateAsync,
    generateBanners.isPending,
    generateBanners.mutate,
  ]);

  const runBannersTransition = useCallback(() => {
    const taglinesChanged =
      JSON.stringify(selectedCampaign?.taglines ?? [{}, {}, {}]) !==
      JSON.stringify(taglinesData);

    const videoData = queryClient.getQueryData(videoKey.detail(videoId));

    const visualsChanged =
      bannersAssetsFingerprintRef.current !== null &&
      bannersAssetsFingerprintRef.current !== assetsFingerprint;

    const isFirstBannerGeneration =
      !videoData?.templateAssetsRenditions?.length;

    if (isFirstBannerGeneration && !taglinesChanged && !visualsChanged) {
      if (!generateBanners.isPending) {
        generateBanners.mutate({
          videoId,
        });
      }
      return;
    }

    startTransition(async () => {
      if (taglinesChanged) {
        const editedTaglines =
          selectedCampaign?.taglines?.map((tagline, index) => ({
            ...tagline,
            ...(taglinesData?.[index] || {}),
          })) ?? [];

        await editCampaign.mutateAsync({
          ...selectedCampaign,
          taglines: editedTaglines,
        });
      }

      const shouldRegenerateBanners =
        isFirstBannerGeneration || taglinesChanged || visualsChanged;

      if (!shouldRegenerateBanners) {
        return;
      }

      await regenerateBannersFromCurrentAssets();
    });
  }, [
    selectedCampaign,
    taglinesData,
    editCampaign.mutateAsync,
    queryClient,
    videoId,
    assetsFingerprint,
    generateBanners,
    regenerateBannersFromCurrentAssets,
  ]);

  // ---------------------------------------------------------------------------
  // Voiceover mux
  // ---------------------------------------------------------------------------

  const computeVoiceoverMuxKey = useCallback(
    (videoData) => {
      const audioVariations = template.data?.audioVariations;
      if (!audioVariations?.length) {
        return null;
      }
      if (
        videoData?.mogrtRootPath &&
        videoData?.templateType === "mogrt" &&
        describeMogrtTemplate.isLoading
      ) {
        return null;
      }

      const voiceoverMode = videoData?.voiceoverMode;
      const musicPath =
        videoData?.musicPath ?? describeMogrtTemplate.data?.musicPath ?? "";

      if (voiceoverMode === "none") {
        const noVoMusicPath =
          describeMogrtTemplate.data?.musicNoVoPath ?? musicPath;
        return noVoMusicPath ? `none:${noVoMusicPath}` : null;
      }

      if (voiceoverMode === "upload") {
        return null;
      }

      const sourceAssets = audioVariations.map((v) =>
        videoData?.assets?.find(
          (a) => a.type === "audio" && a.name === v.referenceKey,
        ),
      );
      if (sourceAssets.some((a) => !a?.filePath)) {
        return null;
      }
      return getAudioMuxSourcesKey(sourceAssets, musicPath);
    },
    [
      template.data?.audioVariations,
      describeMogrtTemplate.isLoading,
      describeMogrtTemplate.data?.musicPath,
      describeMogrtTemplate.data?.musicNoVoPath,
    ],
  );

  const isVoiceoverMuxReady = useCallback(() => {
    const videoData = queryClient.getQueryData(videoKey.detail(videoId));
    const muxPathsKey = computeVoiceoverMuxKey(videoData);
    if (!muxPathsKey) {
      return true;
    }
    const audioVariations = template.data?.audioVariations;
    const existingMuxAsset = videoData?.assets?.find(
      (a) => a.name === audioVariations?.[0]?.key,
    );
    return existingMuxAsset?.muxSourceKey === muxPathsKey;
  }, [
    queryClient,
    videoId,
    computeVoiceoverMuxKey,
    template.data?.audioVariations,
  ]);

  const runVoiceoverMux = useCallback(async () => {
    if (audioMux.isPending) {
      return;
    }

    const videoData = queryClient.getQueryData(videoKey.detail(videoId));
    const audioVariations = template.data?.audioVariations;
    const muxPathsKey = computeVoiceoverMuxKey(videoData);

    if (!muxPathsKey || !audioVariations?.length) {
      return;
    }

    const existingMuxAsset = videoData?.assets?.find(
      (a) => a.name === audioVariations[0]?.key,
    );
    if (existingMuxAsset?.muxSourceKey === muxPathsKey) {
      audioMuxRanForSourcesKeyRef.current = muxPathsKey;
      return;
    }
    if (audioMuxRanForSourcesKeyRef.current === muxPathsKey) {
      return;
    }

    const voiceoverMode = videoData?.voiceoverMode;
    const musicPath =
      videoData?.musicPath ?? describeMogrtTemplate.data?.musicPath ?? "";
    const noVoMusicPath =
      describeMogrtTemplate.data?.musicNoVoPath ?? musicPath;
    const sourceAssets =
      voiceoverMode !== "none"
        ? audioVariations.map((v) =>
            videoData?.assets?.find(
              (a) => a.type === "audio" && a.name === v.referenceKey,
            ),
          )
        : [];

    if (voiceoverMode !== "none" && sourceAssets.some((a) => !a?.filePath)) {
      return;
    }

    try {
      const settingsDuration =
        audioVariations.reduce(
          (max, v) =>
            Math.max(
              max,
              (v.settings?.inset ?? 0) + (v.settings?.duration ?? 0),
            ),
          0,
        ) + 5;

      if (voiceoverMode === "none") {
        if (!noVoMusicPath) {
          return;
        }
        const noVoFirst = audioVariations[0];
        const noVoSettings = noVoFirst?.settings ?? {};
        await uploadAssets.mutateAsync({
          assets: [
            {
              name: noVoFirst.key,
              type: noVoFirst.type,
              filePath: noVoMusicPath,
              referenceKey: noVoFirst.referenceKey,
              volume: noVoSettings.volume ?? DEFAULT_VOICEOVER_VOLUME,
              inset: 0,
              start: 0,
              duration: settingsDuration,
              muxSourceKey: muxPathsKey,
            },
          ],
        });
        audioMuxRanForSourcesKeyRef.current = muxPathsKey;
        return;
      }

      const voiceoverPreviewPath = template.data?.voiceoverPreviewUrl;
      const hasRegeneratedSegment = sourceAssets.some((a) => a?.isRegenerated);
      const isDefaultVoiceoverScript =
        !!voiceoverPreviewPath && !hasRegeneratedSegment;

      if (isDefaultVoiceoverScript) {
        const first = audioVariations[0];
        const firstSettings = first?.settings ?? {};
        await uploadAssets.mutateAsync({
          assets: [
            {
              name: first.key,
              type: first.type,
              filePath: voiceoverPreviewPath,
              referenceKey: first.referenceKey,
              volume: firstSettings.volume ?? DEFAULT_VOICEOVER_VOLUME,
              inset: 0,
              start: 0,
              duration: settingsDuration,
              muxSourceKey: muxPathsKey,
            },
          ],
        });
        audioMuxRanForSourcesKeyRef.current = muxPathsKey;
        return;
      }

      let voiceAudios = [];
      let outputDuration = settingsDuration;

      if (voiceoverMode !== "none") {
        voiceAudios = await Promise.all(
          audioVariations.map(async (variation, i) => {
            const s = variation?.settings ?? {};
            const presign = await queryClient.query(
              presignURLQueryOptions(sourceAssets[i].filePath),
            );
            if (!presign?.presignURL) {
              return null;
            }
            return {
              source: { url: presign.presignURL },
              volume: s.volume ?? DEFAULT_VOICEOVER_VOLUME,
              inset: s.inset ?? 0,
              start: s.start ?? 0,
              duration: s.duration ?? 0,
            };
          }),
        );

        if (voiceAudios.some((a) => a == null)) {
          return;
        }

        const reachability = await Promise.all(
          voiceAudios.map(async (track) => {
            try {
              const res = await fetch(track.source.url);
              res.body?.cancel?.();
              return res.ok;
            } catch {
              return false;
            }
          }),
        );
        const missingLabels = reachability
          .map((ok, i) =>
            ok ? null : (audioVariations[i]?.label ?? `Segment ${i + 1}`),
          )
          .filter(Boolean);
        if (missingLabels.length > 0) {
          store
            .getState()
            .addToast(
              `Some voiceover segments haven't been generated yet: ${missingLabels.join(", ")}. Generate them before continuing.`,
              { variant: "negative", timeout: 10000 },
            );
          return;
        }

        outputDuration =
          voiceAudios.reduce(
            (max, tr) => Math.max(max, (tr.inset ?? 0) + (tr.duration ?? 0)),
            0,
          ) + 5;
      }

      const audios = [];
      if (musicPath) {
        const musicPresign = await queryClient.query(
          presignURLQueryOptions(musicPath),
        );
        if (!musicPresign?.presignURL) {
          return;
        }

        audios.push({
          source: { url: musicPresign.presignURL },
          volume: MUSIC_BED_VOLUME,
          inset: 0,
          start: 0,
          duration: outputDuration,
        });
      }

      audios.push(...voiceAudios);

      const muxUrl = await audioMux.mutateAsync(audios);

      if (supportsAudioMuxDownloadTemplate(template.data?.id)) {
        setAudioMuxDownloadUrl(muxUrl);
      }

      const first = audioVariations[0];
      const firstSettings = first?.settings ?? {};
      const sourceAsset = sourceAssets[0];

      await uploadAssets.mutateAsync({
        assets: [
          {
            name: first.key,
            type: first.type,
            src: muxUrl,
            referenceKey: first.referenceKey,
            text: sourceAsset?.text,
            parts: sourceAsset?.parts ? { ...sourceAsset.parts } : undefined,
            voiceId: sourceAsset?.voiceId,
            voiceConfig: sourceAsset?.voiceConfig,
            volume: firstSettings.volume ?? DEFAULT_VOICEOVER_VOLUME,
            inset: 0,
            start: 0,
            duration: outputDuration,
            muxSourceKey: muxPathsKey,
          },
        ],
      });

      audioMuxRanForSourcesKeyRef.current = muxPathsKey;
    } catch (e) {
      if (isCancelledError(e)) {
        return;
      }
      console.error("Audio mux failed:", e);
    }
  }, [
    audioMux,
    uploadAssets,
    queryClient,
    videoId,
    template.data,
    describeMogrtTemplate.data?.musicPath,
    describeMogrtTemplate.data?.musicNoVoPath,
    computeVoiceoverMuxKey,
  ]);

  const runGenerateVideo = useCallback(async () => {
    const deadline = Date.now() + 3 * 60 * 1000;
    while (!isVoiceoverMuxReady() && Date.now() < deadline) {
      await new Promise((resolve) => setTimeout(resolve, 250));
    }

    generateVideo.mutate({
      videoId,
      templateAssetsRenditions: exportBanners.data,
    });
  }, [isVoiceoverMuxReady, generateVideo.mutate, videoId, exportBanners.data]);

  // ---------------------------------------------------------------------------
  // Unified ctx — every step action receives the same object
  // ---------------------------------------------------------------------------

  const stepActionCtx = {
    setSelectedTemplateKey,
    video,
    template,
    assetsTemplate,
    createVideo,
    checkCustomer,
    customerBranding,
    generateScenes,
    generateBanners,
    editCampaign,
    audioMux,
    uploadAssets,
    selectedCampaign,
    imageVariations,
    ensureProjectId,
    runCreateVideo: createVideoIfNeeded,
    runCreateCampaign,
    runGenerateBranding,
    runGenerateScenes,
    runBannersTransition,
    runGenerateVideo,
    resetPersonaCampaigns,
    isVoiceoverMuxReady,
    runVoiceoverMux,
    computeVoiceoverMuxKey,
    syncKeyVisual,
    runSyncKeyVisual,
  };

  // ---------------------------------------------------------------------------
  // Dispatch — assign to refs from context so handleNextStep (in the parent)
  // can call these without a direct dependency on the hook output.
  // ---------------------------------------------------------------------------

  // `handleNextStep` dispatches by step id; the actions are resolved from the
  // step's configuration (its `type` decides its family).
  const findActiveStep = (stepId) =>
    activeSteps?.find((step) => step.id === stepId) ?? stepId;

  runStepEnteringActionRef.current = (stepId) => {
    getStepEnterAction(findActiveStep(stepId), activeTemplateId)?.run(
      stepActionCtx,
    );
  };

  runStepLeavingActionRef.current = (stepId) => {
    const action = getStepLeaveAction(findActiveStep(stepId), activeTemplateId);
    if (!action) {
      return;
    }
    if (!action.isReady(stepActionCtx) && !action.isPending(stepActionCtx)) {
      action.run(stepActionCtx);
    }
  };

  return {
    stepActionCtx,
    regenerateBannersFromCurrentAssets,
    computeVoiceoverMuxKey,
  };
};
